import { supabase } from '../supabase/client';
import {
  GlobalReport,
  ReportSummary,
  CountryDestinationData,
  RouteStatistics,
  TimeSeriesData,
  VehicleTypeStats,
  CargoTypeStats,
  ReportFilters,
  CityData
} from '../types/reports';
import { parseAddress } from '../utils/addressParser';
import { COUNTRIES } from '../data/countries';

export const generateGlobalReport = async (filters?: ReportFilters): Promise<GlobalReport> => {
  const startDate = filters?.startDate || new Date(0);
  const endDate = filters?.endDate || new Date();

  let query = supabase
    .from('quotations')
    .select('*')
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (filters?.vehicleTypes && filters.vehicleTypes.length > 0) {
    query = query.in('vehicle_type', filters.vehicleTypes);
  }

  const { data: quotations, error } = await query;

  if (error) {
    console.error('Error fetching quotations:', error);
    throw new Error('Failed to fetch quotations for report');
  }

  if (!quotations || quotations.length === 0) {
    return {
      summary: {
        totalQuotations: 0,
        uniqueUsers: 0,
        totalDistance: 0,
        averageCostPerQuotation: 0,
        dateRange: { start: startDate, end: endDate }
      },
      countryDestinations: [],
      topRoutes: [],
      timeSeries: [],
      vehicleTypeStats: [],
      cargoTypeStats: []
    };
  }

  const summary = generateSummary(quotations, startDate, endDate);
  const countryDestinations = generateCountryDestinations(quotations, filters);
  const topRoutes = generateTopRoutes(quotations);
  const timeSeries = generateTimeSeries(quotations);
  const vehicleTypeStats = generateVehicleTypeStats(quotations);
  const cargoTypeStats = generateCargoTypeStats(quotations);

  return {
    summary,
    countryDestinations,
    topRoutes,
    timeSeries,
    vehicleTypeStats,
    cargoTypeStats
  };
};

const generateSummary = (quotations: any[], startDate: Date, endDate: Date): ReportSummary => {
  const uniqueUsers = new Set(quotations.map(q => q.user_id)).size;
  const totalDistance = quotations.reduce((sum, q) => sum + (q.distance || 0), 0);
  const totalCost = quotations.reduce((sum, q) => sum + (q.total_cost || 0), 0);
  const averageCost = quotations.length > 0 ? totalCost / quotations.length : 0;

  return {
    totalQuotations: quotations.length,
    uniqueUsers,
    totalDistance,
    averageCostPerQuotation: averageCost,
    dateRange: { start: startDate, end: endDate }
  };
};

const generateCountryDestinations = (quotations: any[], filters?: ReportFilters): CountryDestinationData[] => {
  const countryMap = new Map<string, { count: number; cities: Map<string, number> }>();

  quotations.forEach(quotation => {
    const destination = quotation.destination_address || quotation.destination;
    if (!destination) return;

    const parsed = parseAddress(destination);
    if (!parsed.countryCode) return;

    if (filters?.countries && filters.countries.length > 0) {
      if (!filters.countries.includes(parsed.countryCode)) return;
    }

    const existing = countryMap.get(parsed.countryCode);
    if (existing) {
      existing.count++;
      if (parsed.city) {
        const cityCount = existing.cities.get(parsed.city) || 0;
        existing.cities.set(parsed.city, cityCount + 1);
      }
    } else {
      const cities = new Map<string, number>();
      if (parsed.city) {
        cities.set(parsed.city, 1);
      }
      countryMap.set(parsed.countryCode, { count: 1, cities });
    }
  });

  const totalQuotations = quotations.length;
  const result: CountryDestinationData[] = [];

  countryMap.forEach((data, countryCode) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (!country) return;

    const cities: CityData[] = [];
    data.cities.forEach((count, cityName) => {
      cities.push({ cityName, quotationCount: count });
    });

    cities.sort((a, b) => b.quotationCount - a.quotationCount);

    result.push({
      countryCode,
      countryName: country.name,
      quotationCount: data.count,
      percentage: (data.count / totalQuotations) * 100,
      cities: cities.slice(0, 10)
    });
  });

  result.sort((a, b) => b.quotationCount - a.quotationCount);

  return result;
};

const generateTopRoutes = (quotations: any[]): RouteStatistics[] => {
  const routeMap = new Map<string, { count: number; totalDistance: number; totalCost: number; currencies: Map<string, number> }>();

  quotations.forEach(quotation => {
    const origin = quotation.origin_address || quotation.origin;
    const destination = quotation.destination_address || quotation.destination;
    if (!origin || !destination) return;

    const routeKey = `${origin}|||${destination}`;
    const currency = quotation.selected_currency || 'MXN';
    const existing = routeMap.get(routeKey);

    if (existing) {
      existing.count++;
      existing.totalDistance += quotation.distance || 0;
      existing.totalCost += quotation.total_cost || 0;
      const currencyCount = existing.currencies.get(currency) || 0;
      existing.currencies.set(currency, currencyCount + 1);
    } else {
      const currencies = new Map<string, number>();
      currencies.set(currency, 1);
      routeMap.set(routeKey, {
        count: 1,
        totalDistance: quotation.distance || 0,
        totalCost: quotation.total_cost || 0,
        currencies
      });
    }
  });

  const routes: RouteStatistics[] = [];
  routeMap.forEach((data, routeKey) => {
    const [origin, destination] = routeKey.split('|||');

    let mostCommonCurrency = 'MXN';
    let maxCount = 0;
    const currencyCount: Record<string, number> = {};

    data.currencies.forEach((count, currency) => {
      currencyCount[currency] = count;
      if (count > maxCount) {
        maxCount = count;
        mostCommonCurrency = currency;
      }
    });

    routes.push({
      origin,
      destination,
      quotationCount: data.count,
      totalDistance: data.totalDistance,
      averageCost: data.count > 0 ? data.totalCost / data.count : 0,
      averageDistance: data.count > 0 ? data.totalDistance / data.count : 0,
      currency: mostCommonCurrency,
      currencyCount
    });
  });

  routes.sort((a, b) => b.quotationCount - a.quotationCount);

  return routes.slice(0, 20);
};

const generateTimeSeries = (quotations: any[]): TimeSeriesData[] => {
  const dateMap = new Map<string, number>();

  quotations.forEach(quotation => {
    const date = new Date(quotation.created_at);
    const dateKey = date.toISOString().split('T')[0];

    const count = dateMap.get(dateKey) || 0;
    dateMap.set(dateKey, count + 1);
  });

  const timeSeries: TimeSeriesData[] = [];
  dateMap.forEach((count, date) => {
    timeSeries.push({ date, count });
  });

  timeSeries.sort((a, b) => a.date.localeCompare(b.date));

  return timeSeries;
};

const generateVehicleTypeStats = (quotations: any[]): VehicleTypeStats[] => {
  const vehicleMap = new Map<string, number>();

  quotations.forEach(quotation => {
    const vehicleType = quotation.vehicle_type;
    if (!vehicleType) return;

    const count = vehicleMap.get(vehicleType) || 0;
    vehicleMap.set(vehicleType, count + 1);
  });

  const total = quotations.length;
  const stats: VehicleTypeStats[] = [];

  vehicleMap.forEach((count, vehicleType) => {
    stats.push({
      vehicleType,
      count,
      percentage: (count / total) * 100
    });
  });

  stats.sort((a, b) => b.count - a.count);

  return stats;
};

const generateCargoTypeStats = (quotations: any[]): CargoTypeStats[] => {
  const cargoMap = new Map<string, number>();

  quotations.forEach(quotation => {
    const cargoType = quotation.cargo_type;
    if (!cargoType) return;

    const count = cargoMap.get(cargoType) || 0;
    cargoMap.set(cargoType, count + 1);
  });

  const total = quotations.filter(q => q.cargo_type).length;
  const stats: CargoTypeStats[] = [];

  cargoMap.forEach((count, cargoType) => {
    stats.push({
      cargoType,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0
    });
  });

  stats.sort((a, b) => b.count - a.count);

  return stats;
};

export const exportReportToCSV = (report: GlobalReport, language: string = 'es'): string => {
  const translations = {
    es: {
      title: 'Reporte Global de Cotizaciones',
      summary: 'RESUMEN',
      totalQuotations: 'Total de Cotizaciones',
      uniqueUsers: 'Usuarios Únicos',
      totalDistance: 'Distancia Total (km)',
      averageCost: 'Costo Promedio',
      startDate: 'Fecha Inicio',
      endDate: 'Fecha Fin',
      countryDestinations: 'DESTINOS POR PAÍS',
      countryHeaders: 'País,Código,Cotizaciones,Porcentaje',
      topRoutes: 'RUTAS MÁS POPULARES',
      routeHeaders: 'Origen,Destino,Cotizaciones,Distancia Promedio (km),Costo Promedio,Moneda',
      vehicleTypes: 'TIPOS DE VEHÍCULO',
      vehicleHeaders: 'Tipo de Vehículo,Cotizaciones,Porcentaje'
    },
    en: {
      title: 'Global Quotations Report',
      summary: 'SUMMARY',
      totalQuotations: 'Total Quotations',
      uniqueUsers: 'Unique Users',
      totalDistance: 'Total Distance (km)',
      averageCost: 'Average Cost',
      startDate: 'Start Date',
      endDate: 'End Date',
      countryDestinations: 'DESTINATIONS BY COUNTRY',
      countryHeaders: 'Country,Code,Quotations,Percentage',
      topRoutes: 'MOST POPULAR ROUTES',
      routeHeaders: 'Origin,Destination,Quotations,Average Distance (km),Average Cost,Currency',
      vehicleTypes: 'VEHICLE TYPES',
      vehicleHeaders: 'Vehicle Type,Quotations,Percentage'
    }
  };

  const t = translations[language as keyof typeof translations] || translations.es;
  const locale = language === 'es' ? 'es-MX' : 'en-US';

  let csv = `${t.title}\n\n`;

  csv += `${t.summary}\n`;
  csv += `${t.totalQuotations},${report.summary.totalQuotations}\n`;
  csv += `${t.uniqueUsers},${report.summary.uniqueUsers}\n`;
  csv += `${t.totalDistance},${report.summary.totalDistance.toFixed(2)}\n`;
  csv += `${t.averageCost},${report.summary.averageCostPerQuotation.toFixed(2)}\n`;
  csv += `${t.startDate},${report.summary.dateRange.start.toLocaleDateString(locale)}\n`;
  csv += `${t.endDate},${report.summary.dateRange.end.toLocaleDateString(locale)}\n\n`;

  csv += `${t.countryDestinations}\n`;
  csv += `${t.countryHeaders}\n`;
  report.countryDestinations.forEach(country => {
    csv += `${country.countryName},${country.countryCode},${country.quotationCount},${country.percentage.toFixed(2)}%\n`;
  });
  csv += '\n';

  csv += `${t.topRoutes}\n`;
  csv += `${t.routeHeaders}\n`;
  report.topRoutes.forEach(route => {
    csv += `"${route.origin}","${route.destination}",${route.quotationCount},${route.averageDistance.toFixed(2)},${route.averageCost.toFixed(2)},${route.currency}\n`;
  });
  csv += '\n';

  csv += `${t.vehicleTypes}\n`;
  csv += `${t.vehicleHeaders}\n`;
  report.vehicleTypeStats.forEach(stat => {
    csv += `"${stat.vehicleType}",${stat.count},${stat.percentage.toFixed(2)}%\n`;
  });

  return csv;
};
